import React, { useCallback, useState } from 'react';
import { Popover } from 'antd';
import styled from 'styled-components';

import { TCustomerInsights } from 'containers/Intelligence/i18n/TModules';
import { tokens } from 'containers/Intelligence/theme';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

/**
 * The five sections the FR fixes for a card explainer. Deliberately closed: the FR bars dynamic
 * values, formulas, timestamps, confidence methodology, evidence records and consent messages from
 * tooltip copy, so there is no slot to put them in.
 */
export interface CardTooltipContent {
  shows: string;
  includes?: string;
  read: string;
  use: string;
  note?: string;
}

export interface ICardInfoTooltipProps {
  title: string;
  content: CardTooltipContent;
  className?: string;
}

type ContentSection = { field: keyof CardTooltipContent; labelKey: string };

const SECTIONS: ContentSection[] = [
  { field: 'shows', labelKey: 'Shows' },
  { field: 'includes', labelKey: 'Includes' },
  { field: 'read', labelKey: 'Read' },
  { field: 'use', labelKey: 'Use' },
  { field: 'note', labelKey: 'Note' }
];

const PANEL_WIDTH = '618px';
const TRIGGER_SIZE = '28px';

const TooltipHost = styled.span`
  position: relative;
  display: inline-flex;

  /* The overlay is rendered inside this host (see getPopupContainer), so antd's light defaults are
     restyled from here — a body-level portal would also drop out of the --is-* token scope. */

  /* public/*-theme.css is a stale build artifact — compiled from antd 4.24.10 against the 4.24.16
     now installed — and clamps the overlay to max-width 100%, 100% of this 28px host, which
     collapsed the painted background to a sliver beside its own text (PLA-8815). Pin it to the
     viewport, as current antd does. */
  .ant-popover {
    max-width: 100vw;
  }

  .ant-popover-inner {
    background: ${tokens.color['surface-card']};
    border-radius: ${tokens.radius.lg};
    box-shadow: ${tokens.elevation.overlay};
  }

  .ant-popover-inner-content {
    padding: 0;
  }

  .ant-popover-arrow-content {
    --antd-arrow-background-color: ${tokens.color['surface-card']};
    background-color: ${tokens.color['surface-card']};
  }
`;

const InfoButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${TRIGGER_SIZE};
  height: ${TRIGGER_SIZE};
  padding: 0;
  border: 1px solid ${tokens.color['border-subtle']};
  border-radius: ${tokens.radius.pill};
  background: transparent;
  color: ${tokens.color['text-muted']};
  font-size: ${tokens.type['card-title-size']};
  font-weight: ${tokens.type['card-title-weight']};
  line-height: ${tokens.type['card-title-line']};
  cursor: pointer;

  &:focus-visible {
    box-shadow: ${tokens.elevation['focus-ring']};
    outline: none;
  }
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;

  /* A definite width so the overlay's shrink-to-fit resolves to the design measure rather than to
     the host's 28px, capped so a viewport narrower than the panel wraps it instead of overflowing. */
  width: ${PANEL_WIDTH};
  max-width: 100%;
  padding: ${tokens.space['20']} ${tokens.space['24']};

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). The doubled class
     outranks the children's own margin reset, which is injected later and would otherwise win the
     cascade on equal specificity and collapse this gap to nothing. */
  && > * + * {
    margin-top: ${tokens.space['12']};
  }
`;

const PanelTitle = styled.h3`
  margin: 0;
  color: ${tokens.color['text-primary']};
  font-size: ${tokens.type['section-title-size']};
  font-weight: ${tokens.type['section-title-weight']};
  line-height: ${tokens.type['section-title-line']};
`;

const Divider = styled.hr`
  height: 1px;
  margin: 0;
  border: 0;
  background: ${tokens.color['border-subtle']};
`;

const Section = styled.p`
  margin: 0;
  color: ${tokens.color['text-secondary']};
  font-size: ${tokens.type['body-size']};
  font-weight: ${tokens.type['body-weight']};
  line-height: ${tokens.type['body-line']};
`;

const SectionLabel = styled.strong`
  color: ${tokens.color['text-primary']};
`;

const CardInfoTooltip: React.FC<ICardInfoTooltipProps> = ({ title, content, className }) => {
  const { t } = useMultiLanguageTranslation();
  const [open, setOpen] = useState(false);

  const handleOpenChange = useCallback((next: boolean) => setOpen(next), []);

  const renderContent = () => (
    <Panel data-testid="card-info-tooltip-body">
      <PanelTitle data-testid="card-info-tooltip-title">{title}</PanelTitle>
      <Divider />
      {SECTIONS.filter(section => content[section.field]).map(section => (
        <Section key={section.field} data-testid={`card-info-tooltip-${section.field}`}>
          <SectionLabel>{t(`${TCustomerInsights}.Tooltip.${section.labelKey}`)}</SectionLabel>{' '}
          {content[section.field]}
        </Section>
      ))}
    </Panel>
  );

  return (
    <TooltipHost className={className} data-testid="card-info-tooltip">
      <Popover
        open={open}
        onOpenChange={handleOpenChange}
        content={renderContent()}
        // The trigger sits in the card's top-right corner, so the 618px panel has to grow leftwards:
        // anchoring its left edge would push it past the viewport on every card.
        placement="bottomRight"
        // FR: activation is click or tap, never hover-only
        // (conventions/components/cross-browser.md § 1 bars the focus trigger outright).
        trigger="click"
        getPopupContainer={triggerNode => triggerNode.parentElement}
      >
        <InfoButton
          type="button"
          aria-expanded={open}
          aria-label={t(`${TCustomerInsights}.Tooltip.Trigger`, { title })}
          data-testid="card-info-tooltip-trigger"
        >
          i
        </InfoButton>
      </Popover>
    </TooltipHost>
  );
};

export default CardInfoTooltip;
